import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
}                           from 'react';
import styled, {
  keyframes,
}                           from 'styled-components';
import { FontAwesomeIcon }  from '@fortawesome/react-fontawesome';
import {
  faCommentSms,
  faEnvelope,
  faKey,
}                           from '@fortawesome/pro-solid-svg-icons';
import {
  faWhatsapp,
}                           from '@fortawesome/free-brands-svg-icons';
import useJwt               from 'hooks/useJwt';
import { useUserAgent }     from 'hooks/useUserAgent';
import {
  FormattedMessage,
  useIntl,
}                           from 'react-intl';

import 'react-toggle/style.css';
import './index.css';

const WEB_CLIENT_BASE_URL = process.env.MAPP_WEB_CLIENT_BASE_URL;

const VERIFICATION_CODE_LENGTH = 6;
const VERIFICATION_CODE_GAP = '0.25em';

const mfaChallenge = async (method, token) => (
  fetch(`${WEB_CLIENT_BASE_URL}mfa/challenge?${new URLSearchParams({ token, method })}`)
    .then(r => r.ok ? r.json() : r.text().then(t => Promise.reject(t)))
);

const method_icons = {
  'sms': faCommentSms,
  'email': faEnvelope,
  'whatsapp': faWhatsapp,
  'rfc6238': faKey,
};
const method_translations = {
  'sms': 'SMS',
  'email': 'E-Mail',
  'whatsapp': 'Whatsapp',
  'rfc6238': 'Google Authenticator / Authy',
};

const getMFAResponse = (mfa_challenge_token, code, remember_user) => (
  fetch(`/mfa/response`, {
    method: "POST",
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      mfa_challenge_token,
      response: { code },
      remember_me: remember_user,
    }),
  })
);

const fetchBasicCookie = (login_token, mfa_response_token) => (
  fetch(`/auth/basic_cookie`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      login_token,
      mfa_response_token,
    }),
  })
);

const background_blink = keyframes`
  from, to {
    background-color: transparent;
  }
  50% {
    background-color: var(--content-1);
  }
`;

const InputBlinkWrapper = styled.div`
  position: relative;
  flex-shrink: 1;
  min-width: 0;

  &:after {
    content: "";
    position: absolute;
    height: 70%;
    width: 2px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation: 1s ${background_blink} step-end ${props => props.blink ? 'infinite' : 0};
  }
`;


const SELECTING_METHOD_STEP = 0;
const TRUSTING_DEVICE_STEP = 1;
const CHALLENGING_MFA_STEP = 2;

const MethodScreen = ({ onMethodChosen }) => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const { decoded_token } = useJwt(token);
  const { available_mfa_methods } = decoded_token || {};

  return (
    <div style={{textAlign: 'center'}}>
      <h2>
        <FormattedMessage defaultMessage='Por favor, selecciona un método de autenticación:' />
      </h2>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        border: `1px solid ${Colors.content_1}`,
        borderRadius: '0.4em',
        overflow: 'hidden',
        margin: '1em',
      }}>
        {(available_mfa_methods || []).map(method => (
          <div
            key={method}
            className='hover-background_light_2'
            style={{
              padding: '2rem',
              outline: `1px solid ${Colors.content_1}`,
              backgroundColor: Colors.background_light_1,
              cursor: 'pointer',
              textAlign: 'center',
              borderRadius: '0px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5em',
            }}
            onClick={async () => {
              onMethodChosen(method);
            }}
          >
            {method_icons[method] ? <FontAwesomeIcon style={{fontSize: '1.5em'}} icon={method_icons[method]}/> : null}
            {method_translations[method]}
          </div>
        ))}
      </div>
    </div>
  );
};

const TrustDeviceScreen = ({ onTrustSelected }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '1em',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: Fonts.size.extra_large, fontWeight: Fonts.weight.bold }}>
        <FormattedMessage defaultMessage={'¿Confiar en este navegador por 30 días?'}/>
      </div>
      <FormattedMessage defaultMessage={'Si confías en este navegador no se te pedirá el código de verificación la próxima vez que inicies sesión.'}/>
      <div style={{ width: '100%', display: 'flex', justifyContent: 'end', alignItems: 'center', gap: '1em' }}>
        <button
          className='button-medium'
          onClick={() => onTrustSelected(false)}
          style={{ fontSize: '1em', marginRight: 'auto' }}
        >
          <FormattedMessage defaultMessage={'Ahora no'}/>
        </button>
        <button
          className='button-medium'
          style={{ fontSize: '1em' }}
          onClick={() => onTrustSelected(false)}
        >
          <FormattedMessage defaultMessage={'No confiar'}/>
        </button>
        <button
          className='button-medium button-primary'
          style={{ fontSize: '1em' }}
          onClick={() => onTrustSelected(true)}
        >
          <FormattedMessage defaultMessage={'Confiar'}/>
        </button>
      </div>
    </div>
  );
};

const ChallengeScreen = ({ method, remember_user }) => {

  const { is_mobile } = useUserAgent();
  const params = new URLSearchParams(window.location.search);

  const token = params.get('token');
  const mobile_app = params.get('mobile_app');
  const embedded = params.get('embedded');

  const verification_code_input = useRef();

  const { formatMessage } = useIntl();
  const [ challenge_token, setChallengeToken ] = useState('');
  const [ input_focused, setInputFocused ] = useState(false);
  const [ verification_code, setVerificationCode ] = useState('');
  const [ is_verifying, setIsVerifying ] = useState(false);
  const [ failed_mfa_challenge, setFailedMFAChallenge ] = useState(false);
  const [ error, setError ] = useState('');

  const getChallengeToken = useCallback(async () => {
    mfaChallenge(method, token)
      .then(({ challenge_token }) => {
        setChallengeToken(challenge_token);
      })
      .catch(err => {
        if (err === 'JWT_EXPIRED') window.location.replace(new URL(window.location).origin);
      });
  }, [ method, token ]);

  useEffect(() => {
    if (challenge_token) return;
    getChallengeToken();
  }, [challenge_token, getChallengeToken]);

  useEffect(() => {
    if (is_verifying) return;
    verification_code_input.current?.focus();
  }, [is_verifying]);

  const { decoded_token: decoded_challenge_token } = useJwt(challenge_token);
  const { hint } = decoded_challenge_token || {};

  useEffect(() => {
    if (method !== 'sms' || !('OTPCredential' in window)) return;
    const startCodeRetrieval = async () => {
      const { code } = await navigator.credentials.get({otp: {transport: ["sms"]}});
      setVerificationCode(code);
    };
    startCodeRetrieval();
  }, [ method ]);

  const submitChallenge = useCallback(async () => {
    if (is_verifying) return;
    setIsVerifying(true);
    const response_token = await getMFAResponse(challenge_token, verification_code, remember_user)
      .then(r => r.ok ? r.json() : r.text().then(t => Promise.reject(t)))
      .catch(err => {
        setVerificationCode('');
        setFailedMFAChallenge(true);
        setIsVerifying(false);
        if (err === 'JWT_EXPIRED') window.location.replace(new URL(window.location).origin);
        return Promise.reject(err);
      });

    if (mobile_app) {
      switch (mobile_app) {
        // Since Android is injected by Walcu Phone, we need to disable the eslint rule
        // eslint-disable-next-line no-undef
        case "WalcuPhone": Android.loginMFA(token, response_token); break;
        case "WalcuVoip": window.ReactNativeWebView?.postMessage?.(response_token); break;
        default: break;
      }
    } else if (embedded) {
      window.top?.postMessage?.({
        token: token,
        response_token,
      }, '*');
    } else {
      fetchBasicCookie(token, response_token)
        .then(res => res.ok && res.json() || res.text().then(t =>
          Promise.reject({
            message: t,
            mfa_challenge_token: res.headers.get('x-mfa-challenge-token'),
          })
        ))
        .then((decoded_token) => {
          if (decoded_token) {
            localStorage.setItem('decoded_token', JSON.stringify(decoded_token));
            if (localStorage.getItem('last_path')) {
              const last_path = localStorage.getItem('last_path');
              localStorage.removeItem('last_path');
              window.location.replace(last_path);
            } else {
              window.location.replace(is_mobile ? '/mobile' : '/crm');
            }
          }
        })
        .catch(err => {
          setError(err.message);
        });
    }
  }, [challenge_token, verification_code, remember_user, mobile_app, embedded, token, is_verifying, is_mobile]);

  useEffect(() => {
    if (verification_code.length === VERIFICATION_CODE_LENGTH && !failed_mfa_challenge) {
      submitChallenge();
    }
  }, [verification_code, submitChallenge, failed_mfa_challenge]);

  useEffect(() => { verification_code?.length && setFailedMFAChallenge(false) }, [verification_code]);

  const getHintText = (method, hint) => {
    switch (method) {
      case 'email':
        return formatMessage({ defaultMessage: 'Introduce el código de verificación que se ha enviado por correo a {hint}' }, { hint });
      case 'sms':
        return formatMessage({ defaultMessage: 'Introduce el código de verificación que se ha enviado por SMS al número terminado en {hint}' }, { hint });
      case 'whatsapp':
        return formatMessage({ defaultMessage: 'Introduce el código de verificación que se ha enviado por WhatsApp al número terminado en {hint}' }, { hint });
      case 'rfc6238':
      default:
        return null;
    }
  };
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      gap: '1em',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'center',
    }}>
      <h2>{getHintText(method, hint)}</h2>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5em',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{
          position: 'relative',
          height: '2em',
          width: `min(100%, calc(${VERIFICATION_CODE_LENGTH} * 1.5em + ${VERIFICATION_CODE_LENGTH - 1} * ${VERIFICATION_CODE_GAP}))`,
          fontSize: '2.5em',
          animation: failed_mfa_challenge ? 'horizontal-shaking 0.25s ease-in-out 3' : 'none',
        }}>
          <input
            ref={verification_code_input}
            autoFocus
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            autoComplete='one-time-code'
            maxLength={VERIFICATION_CODE_LENGTH}
            style={{
              height: '100%',
              width: '100%',
              opacity: 0,
              caretColor: Colors.content_1,
              fontFamily: 'ui-monospace, monospace',
              fontSize: '1em',
            }}
            value={verification_code}
            onChange={e => setVerificationCode(e.target.value.split('').filter(c => !isNaN(c)).join(''))}
            onKeyDown={e => {
              const length = e.target.value.length;
              e.target.setSelectionRange(length, length);
            }}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            disabled={is_verifying}
          />
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: VERIFICATION_CODE_GAP,
            zIndex: Z.hidden,
            overflow: 'hidden',
          }}>
            {new Array(VERIFICATION_CODE_LENGTH).fill(0).map((_, i) => (
              <InputBlinkWrapper blink={input_focused && (verification_code.length === i)}>
                <input
                  disabled
                  key={i}
                  value={verification_code.toString().charAt(i)}
                  style={{
                    width: '100%',
                    height: '100%',
                    textAlign: 'center',
                    fontSize: '1em',
                    backgroundColor: is_verifying ? Colors.content_1 : (failed_mfa_challenge ? `rgba(${Colors.accent_6_rgb},0.2)` : Colors.background_light_1),
                    borderRadius: '8px',
                    border: '2px solid',
                    borderColor: failed_mfa_challenge ? Colors.accent_6 : (
                      input_focused && (verification_code.length === i) ? Colors.content_1 : 'transparent'
                    ),
                  }}
                />
              </InputBlinkWrapper>
            ))}
          </div>
        </div>
        {failed_mfa_challenge && <div style={{fontSize: '1.3em', color: Colors.accent_6}}>
          <FormattedMessage defaultMessage='Código introducido incorrecto'/>
        </div>}
        {error && <div style={{fontSize: '1.3em', color: Colors.accent_6}}>
          {error}
        </div>}
        <a
          style={{
            display: 'flex',
            gap: '0.5em',
            alignItems: 'center',
            userSelect: 'none',
          }}
          onClick={() => getChallengeToken()}
        >
          <FormattedMessage defaultMessage='Volver a enviar el código'/>
        </a>
      </div>
    </div>
  );
};

const MFA = () => {
  const [ current_step, setCurrentStep ] = useState(SELECTING_METHOD_STEP);
  const [ chosen_method, setChosenMethod ] = useState(null);
  const [ remember_user, setRememberUser ] = useState(false);

  return (
    <div style={{
      width: 'max(50%, 25em)',
      maxWidth: '90%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '2em',
      paddingTop: 'max(15vh, 5em)',
      textAlign: 'center',
    }}>
      <img style={{ maxWidth: '90%', height: '4em' }} src={'/static/logo-dark.svg'}/>
      {current_step === SELECTING_METHOD_STEP && (
        <MethodScreen
          onMethodChosen={m => {
            setChosenMethod(m);
            setCurrentStep(TRUSTING_DEVICE_STEP);
          }}
        />
      ) || current_step === TRUSTING_DEVICE_STEP && (
        <TrustDeviceScreen
          onTrustSelected={t => {
            setRememberUser(t);
            setCurrentStep(CHALLENGING_MFA_STEP);
          }}
        />
      ) || current_step === CHALLENGING_MFA_STEP && (
        <ChallengeScreen
          method={chosen_method}
          remember_user={remember_user}
        />
      )}
    </div>
  );

};

export default MFA;
